import Collapsible from "react-collapsible";
import Input from "./components/Input";
import { MinusCircleIcon } from "@heroicons/react/20/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { LOCAL_PREFIX } from "../../utils/constants";
import ConfirmModal from "../common/ConfirmModal";
import { getHighestLevelWithPrefix } from "./utils/parseProblems";
import { levelToColor, levelToIconName } from "./components/ProblemIndicator";

function getConfirmMessage(toDelete) {
  if (toDelete.isSaved) {
    return `This will remove "${toDelete.name}". After saving you will not be able to recover this change. If unsaved, you can reload the page to recover this application.`;
  }

  return `This will permanently remove "${toDelete.name}". This cannot be undone.`;
}

export default function SoilLayerTableItem({
  sampleId,
  index,
  item,
  onRemove,
  onChange,
  formProblems,
  setAndClearProblem,
}) {
  const [top, setTop] = useState(item.top ?? "");
  const [bottom, setBottom] = useState(item.bottom ?? "");

  const [calcium, setCalcium] = useState(item.calcium ?? "");
  const [cations, setCations] = useState(item.cations ?? "");
  const [magnesium, setMagnesium] = useState(item.magnesium ?? "");
  const [nitrate, setNitrate] = useState(item.nitrate ?? "");
  const [organicMatter, setOrganicMatter] = useState(item.organicMatter ?? "");
  const [ph, setPh] = useState(item.ph ?? "");
  const [phosphate, setPhosphate] = useState(item.phosphate ?? "");
  const [potassium, setPotassium] = useState(item.potassium ?? "");
  const [sodium, setSodium] = useState(item.sodium ?? "");
  const [solubleSalts, setSolubleSalts] = useState(item.solubleSalts ?? "");
  const [sulfate, setSulfate] = useState(item.sulfate ?? "");

  const [toDelete, setToDelete] = useState(undefined);

  useEffect(() => {
    onChange({
      id: item.id, top, bottom, calcium, cations, magnesium, nitrate, ph,
      organicMatter, phosphate, potassium, sodium, solubleSalts, sulfate,
    });
  }, [
    top, bottom, calcium, cations, magnesium, nitrate, organicMatter,
    ph, phosphate, potassium, sodium, solubleSalts, sulfate, item.id,
  ]);

  const prefix = `soil-${sampleId}-layer-${index + 1}`;

  const problemLevel = getHighestLevelWithPrefix(formProblems, prefix, true);

  return (<>
    <Collapsible
      className={`flex flex-col border-t border-gray-300`}
      openedClassName={`flex flex-col border-t border-gray-300`}
      contentOuterClassName="w-full flex flex-col"
      contentInnerClassName="flex-1 ml-4"
      triggerClassName="w-full group"
      triggerOpenedClassName="flex w-full group"
      triggerTagName="div"
      trigger={(
        <div className="flex w-full cursor-auto">
          <div
            className="w-28 flex items-center justify-center p-2 cursor-pointer relative"
            title="Click to expand"
          >
            <FontAwesomeIcon
              icon="chevron-circle-down"
              size="lg"
              className="absolute group-[.is-closed]:-rotate-90 transition-all text-dark-blue left-2.5"
            />
            {index + 1}
            {problemLevel !== "" && (
              <div className="absolute left-20">
                <FontAwesomeIcon
                  icon={levelToIconName(problemLevel || "error")}
                  className={`${levelToColor(problemLevel || "error")}`}
                />
              </div>
            )}
          </div>
          <div
            className="flex-1 flex items-center justify-center p-2"
            // Stop clicking in this div from expanding/collapsing
            onClickCapture={(e) => { e.stopPropagation() }}
          >
            <Input
              name={`${prefix}-top-depth`}
              type="number"
              disabled={index === 0}
              value={top}
              onChange={(v) => setAndClearProblem(setTop, v, `${prefix}-top-depth`)}
              formErrors={formProblems}
            />
          </div>
          <div
            className="flex-1 flex items-center justify-center p-2"
            onClickCapture={(e) => { e.stopPropagation() }}
          >
            <Input
              name={`${prefix}-bottom-depth`}
              type="number"
              value={bottom}
              onChange={(v) => setAndClearProblem(setBottom, v, `${prefix}-bottom-depth`)}
              formErrors={formProblems}
            />
          </div>
          <div
            className={`w-10 flex items-center justify-center pl-0`}
            onClickCapture={(e) => {
              e.stopPropagation();
              setToDelete({
                name: `Layer ${index + 1}`,
                confirm: onRemove,
                isSaved: !item.id.startsWith(LOCAL_PREFIX),
              });
            }}
          >
            {index > 0 ? (
              <MinusCircleIcon className="w-5 h-5 cursor-pointer text-red-600" />
            ) : <div className="w-10" />}
          </div>
        </div>
      )}
      // Open by default so people know it's here
      open
    >
      <div className="pb-2 pr-2">
        <div className="flex gap-2">
          <Input
            label="Soil pH"
            name={`${prefix}-ph`}
            type="number"
            value={ph}
            onChange={(v) => setAndClearProblem(setPh, v, `${prefix}-ph`)}
            formErrors={formProblems}
          />
          <Input
            label="Soluble Salts"
            name={`${prefix}-soluble-salts`}
            type="number"
            suffix="mmho/cm"
            value={solubleSalts}
            onChange={(v) => setAndClearProblem(setSolubleSalts, v, `${prefix}-soluble-salts`)}
            formErrors={formProblems}
          />
          <Input
            label="Organic Matter"
            name={`${prefix}-organic-matter`}
            type="number"
            suffix="%"
            value={organicMatter}
            onChange={(v) => setAndClearProblem(setOrganicMatter, v, `${prefix}-organic-matter`)}
            formErrors={formProblems}
          />
        </div>
        <div className="flex gap-2">
          <Input
            label="Nitrate"
            name={`${prefix}-nitrate`}
            type="number"
            suffix="ppm N"
            value={nitrate}
            onChange={(v) => setAndClearProblem(setNitrate, v, `${prefix}-nitrate`)}
            formErrors={formProblems}
          />
          <Input
            label="Phosphorous"
            name={`${prefix}-phosphorous`}
            type="number"
            suffix="ppm P"
            value={phosphate}
            onChange={(v) => setAndClearProblem(setPhosphate, v, `${prefix}-phosphorous`)}
            formErrors={formProblems}
          />
          <Input
            label="K (Potassium)"
            name={`${prefix}-potassium`}
            type="number"
            suffix="ppm"
            value={potassium}
            onChange={(v) => setAndClearProblem(setPotassium, v, `${prefix}-potassium`)}
            formErrors={formProblems}
          />
        </div>
        <div className="flex gap-2">
          <Input
            label="Ca (Calcium)"
            name={`${prefix}-calcium`}
            type="number"
            suffix="ppm"
            value={calcium}
            onChange={(v) => setAndClearProblem(setCalcium, v, `${prefix}-calcium`)}
            formErrors={formProblems}
          />
          <Input
            label="Mg (Magnesium)"
            name={`${prefix}-magnesium`}
            type="number"
            suffix="ppm"
            value={magnesium}
            onChange={(v) => setAndClearProblem(setMagnesium, v, `${prefix}-magnesium`)}
            formErrors={formProblems}
          />
          <Input
            label="Na (Sodium)"
            name={`${prefix}-sodium`}
            type="number"
            suffix="ppm"
            value={sodium}
            onChange={(v) => setAndClearProblem(setSodium, v, `${prefix}-sodium`)}
            formErrors={formProblems}
          />
        </div>
        <div className="flex gap-2">
          <Input
            label="Sulfate"
            name={`${prefix}-sulfate`}
            type="number"
            suffix="ppm"
            value={sulfate}
            onChange={(v) => setAndClearProblem(setSulfate, v, `${prefix}-sulfate`)}
            formErrors={formProblems}
          />
          <Input
            label="Sum of Cations"
            name={`${prefix}-cations`}
            type="number"
            suffix="me/100g"
            value={cations}
            onChange={(v) => setAndClearProblem(setCations, v, `${prefix}-cations`)}
            formErrors={formProblems}
          />
        </div>
      </div>
    </Collapsible>
    <ConfirmModal
      isOpen={!!toDelete}
      title="Confirm Delete Sample Layer"
      message={toDelete ? getConfirmMessage(toDelete) : ""}
      confirmButtonLabel="Yes, Delete"
      toggle={() => setToDelete(undefined)}
      onConfirm={() => {
        toDelete.confirm();
        setToDelete(undefined);
      }}
      secondaryButtonText="Cancel"
      warning
    />
  </>);
}
